import { safeCuratedMetaReturnPath } from './components/CuratedMetaConnection.jsx';

export const COMPANY_META_SETUP_ENDPOINT = '/api/saas/meta/setup';
const RESUME_KEY = 'lpam:company-meta-setup-launch';
const text = (value) => typeof value === 'string' ? value.trim() : '';
const SELECTION_FIELDS = ['credentialId', 'adAccountResourceId', 'pageResourceId', 'instantFormResourceId', 'instagramIdentityResourceId'];

export function setupSelection(value = {}) {
  return Object.fromEntries(SELECTION_FIELDS.map((field) => [field, text(value?.[field])]));
}

export function setupEditor(snapshot = {}) {
  const binding = snapshot.pending || snapshot.active || {};
  const profile = snapshot.profile || {};
  return {
    selection: setupSelection(binding.selection),
    privacyPolicyUrl: text(binding.status === 'pending' || snapshot.pending ? binding.privacyPolicyUrl : profile.privacyPolicyUrl || binding.privacyPolicyUrl),
    followUpUrl: text(binding.status === 'pending' || snapshot.pending ? binding.followUpUrl : profile.followUpUrl || profile.websiteUrl || binding.followUpUrl),
  };
}

export function setupCheckpointPayload(snapshot, editor) {
  return {
    ...(snapshot?.pending?.id ? { setupId: snapshot.pending.id, expectedRevision: snapshot.pending.revision } : {}),
    expectedActiveSetupId: snapshot?.active?.id || null,
    selection: setupSelection(editor.selection),
    profileRevision: snapshot?.profile?.revision ?? 0,
    privacyPolicyUrl: text(editor.privacyPolicyUrl),
    followUpUrl: text(editor.followUpUrl),
  };
}

export function setupRevisionPayload(snapshot, extra = {}) {
  if (!snapshot?.pending?.id || !Number.isSafeInteger(snapshot.pending.revision)) {
    throw new Error('Save setup progress before continuing.');
  }
  return { setupId: snapshot.pending.id, expectedRevision: snapshot.pending.revision, ...extra };
}

export function companyProfileFormUpdateAvailable(snapshot) {
  if (typeof snapshot?.formUpdateAvailable === 'boolean') return snapshot.formUpdateAvailable;
  const active = snapshot?.active;
  const profile = snapshot?.profile;
  return Boolean(active && profile && (
    (text(profile.privacyPolicyUrl) && text(profile.privacyPolicyUrl) !== text(active.privacyPolicyUrl))
    || (text(profile.followUpUrl) && text(profile.followUpUrl) !== text(active.followUpUrl))
  ));
}

export function setupReady(binding) {
  return binding?.readiness?.ready === true || binding?.readiness?.state === 'ready';
}

// OGC-434: the summary tag follows the server's state-driven readiness. Only
// `needs_attention` is a warning; `stale` is a neutral nudge to re-check and
// carries no failure copy of its own.
const SUMMARY_STATUS = Object.freeze({
  ready: { label: 'Ready', tone: 'ready' },
  stale: { label: 'Re-check recommended', tone: 'stale' },
  checking: { label: 'Checking…', tone: 'stale' },
  needs_attention: { label: 'Needs attention', tone: 'warn' },
  incomplete: { label: 'Setup incomplete', tone: 'warn' },
});
export function setupSummaryStatus(binding, readiness = binding?.readiness) {
  if (setupReady({ readiness })) return { state: 'ready', ...SUMMARY_STATUS.ready };
  const state = typeof readiness?.state === 'string' && SUMMARY_STATUS[readiness.state] ? readiness.state : binding ? 'needs_attention' : 'incomplete';
  return { state, ...SUMMARY_STATUS[state] };
}

export function companySetupResumeStage(snapshot) {
  const pending = snapshot?.pending;
  if (!pending) return 0;
  if (pending.operationId || pending.confirmedAt) return 3;
  return pending.selection?.adAccountResourceId && pending.selection?.pageResourceId ? 1 : 0;
}

export function setupReadinessRows(readiness) {
  const source = Array.isArray(readiness?.checks) ? readiness.checks : [];
  return source.filter((item) => typeof item?.label === 'string').map((item) => ({
    key: text(item.key || item.code || item.label),
    label: text(item.label),
    ready: item.ready === true || ['ready', 'passed', 'valid'].includes(item.state || item.status),
  }));
}

export function setupBindingRows(binding) {
  if (!binding) return [];
  const destination = binding.destination || {};
  const selection = binding.selection || {};
  return [
    ['Ad account', destination.adAccount, selection.adAccountResourceId],
    ['Facebook Page', destination.page, selection.pageResourceId],
    ['Instant Form', destination.instantForm, selection.instantFormResourceId],
    ['Instagram', destination.instagramIdentity, selection.instagramIdentityResourceId],
  ].map(([label, asset, resourceId]) => ({
    label,
    value: text(asset?.label || asset?.name) || (resourceId ? 'Saved resource · access needs verification' : label === 'Instagram' ? 'Facebook Page identity only' : 'Setup incomplete'),
  }));
}

export function campaignSetupBinding(configurationResponse, options, draft) {
  if (configurationResponse?.companyMetaSetup) return configurationResponse.companyMetaSetup;
  const recorded = configurationResponse?.configuration?.meta || {};
  const target = configurationResponse?.metaTarget || configurationResponse?.configuration?.metaTarget;
  if (target?.destination) return { ...target, selection: recorded };
  // A recorded campaign never falls back to today's company default.
  const selection = setupSelection(recorded.adAccountResourceId || recorded.instantFormResourceId ? recorded : draft?.meta);
  if (!selection.adAccountResourceId && !selection.instantFormResourceId) return null;
  const connection = (options?.metaConnections || []).find((candidate) => candidate.adAccounts?.some((account) => account.resourceId === selection.adAccountResourceId));
  const form = connection?.instantForms?.find((candidate) => candidate.resourceId === selection.instantFormResourceId);
  return {
    selection,
    destination: {
      adAccount: connection?.adAccounts?.find((candidate) => candidate.resourceId === selection.adAccountResourceId),
      page: form?.owningPage,
      instantForm: form,
      instagramIdentity: connection?.instagramIdentities?.find((candidate) => candidate.resourceId === selection.instagramIdentityResourceId),
    },
  };
}

export function setupResourceChoices(response, selection) {
  const connections = response?.metaConnections || response?.connections || [];
  const setup = response?.instantFormSetup || {};
  const accounts = connections.flatMap((connection) => (connection.adAccounts || []).map((account) => ({ ...account, credentialId: connection.credentialId })));
  const connection = connections.find((item) => item.credentialId === selection.credentialId);
  const bindings = [...(setup.assignments || []), ...(setup.assignmentCandidates || [])];
  const pages = (connection?.pages || []).filter((page) => bindings.some((binding) => binding.credentialId === connection.credentialId && binding.pageResourceId === page.resourceId));
  const assigned = (setup.assignments || []).some((binding) => binding.credentialId === selection.credentialId && binding.pageResourceId === selection.pageResourceId);
  const pageBinding = bindings.find((binding) => binding.credentialId === selection.credentialId && binding.pageResourceId === selection.pageResourceId);
  const forms = (connection?.instantForms || []).filter((form) => form.owningPage?.resourceId === selection.pageResourceId && form.managedByBeyondLeads === true && (form.selectable || form.selectableForVerification));
  const instagram = (connection?.instagramIdentities || []).filter((identity) => identity.owningPage?.resourceId === selection.pageResourceId && (identity.selectable || identity.selectableForVerification));
  return { accounts, pages, forms, instagram, assigned, verificationRequired: pageBinding?.verificationRequired === true };
}

export function prefillSetupSelection(response, selection, { preserveForm = false } = {}) {
  const next = setupSelection(selection);
  let choices = setupResourceChoices(response, next);
  const connection = (response?.connections || response?.metaConnections || []).find(c => c.credentialId === next.credentialId);
  if (response && !preserveForm && next.instantFormResourceId
    && !(connection?.instantForms || []).some(form => form.resourceId === next.instantFormResourceId && form.managedByBeyondLeads === true
      && form.owningPage?.resourceId === next.pageResourceId)) next.instantFormResourceId = '';
  const accounts = choices.accounts.filter((account) => account.selectable === true);
  if (!next.adAccountResourceId && accounts.length === 1) {
    next.adAccountResourceId = accounts[0].resourceId;
    next.credentialId = accounts[0].credentialId;
    choices = setupResourceChoices(response, next);
  }
  if (!next.pageResourceId && choices.pages.length === 1) next.pageResourceId = choices.pages[0].resourceId;
  choices = setupResourceChoices(response, next);
  if (!next.instantFormResourceId) {
    const preferred = choices.forms.filter(form => form.recommendedForSetup === true);
    if (preferred.length === 1) next.instantFormResourceId = preferred[0].resourceId;
    else if (choices.forms.length === 1) next.instantFormResourceId = choices.forms[0].resourceId;
  }
  // Instagram stays a deliberate optional choice, including with one identity.
  return next;
}

// `connectStep` keeps the Connect Meta step in the progress indicator after the
// browser returns from Meta (OGC-527); it is presentation only, never evidence.
export function rememberCompanyMetaSetupLaunch({ companyId, surface = 'company', campaignId = '', connectStep = false, storage = globalThis.sessionStorage } = {}) {
  if (!text(companyId) || !storage?.setItem) return;
  const returnTo = safeCuratedMetaReturnPath({ surface, campaignId });
  storage.setItem(RESUME_KEY, JSON.stringify({ companyId: text(companyId), returnTo, ...(connectStep === true ? { connectStep: true } : {}) }));
}

// Returns null, or the exact launch hint for this company and surface.
export function consumeCompanyMetaSetupLaunch({ companyId, surface = 'company', campaignId = '', storage = globalThis.sessionStorage } = {}) {
  try {
    const saved = JSON.parse(storage?.getItem(RESUME_KEY) || 'null');
    const exact = saved?.companyId === text(companyId) && (saved?.returnTo === safeCuratedMetaReturnPath({ surface, campaignId }) || (surface === 'company' && saved?.returnTo === '/#/company'));
    if (!exact) return null;
    storage.removeItem(RESUME_KEY);
    return Object.freeze({ connectStep: saved.connectStep === true });
  } catch { return null; }
}

// OGC-527: self-service onboarding has no OAuth return surface of its own. It
// returns through the allowlisted Company callback; this hint, bound to the
// signed-in user and exact company, only changes where verified completion
// continues (a new campaign at Creative). It never asserts readiness.
const ONBOARDING_KEY = 'lpam:onboarding-meta-continuation';
const ONBOARDING_TTL_MS = 2 * 60 * 60 * 1000;

export function rememberOnboardingMetaContinuation({ userId, companyId, storage = globalThis.sessionStorage, now = Date.now() } = {}) {
  if (!text(userId) || !text(companyId) || !storage?.setItem) return false;
  storage.setItem(ONBOARDING_KEY, JSON.stringify({ userId: text(userId), companyId: text(companyId), savedAt: now }));
  return true;
}

export function onboardingMetaContinuationActive({ userId, companyId, storage = globalThis.sessionStorage, now = Date.now() } = {}) {
  try {
    const saved = JSON.parse(storage?.getItem(ONBOARDING_KEY) || 'null');
    if (!saved) return false;
    // Another user, company, or an expired hint never redirects. Reading is
    // side-effect free (it runs during render); completion, close and
    // sign-out clear the hint.
    return saved.userId === text(userId) && saved.companyId === text(companyId)
      && Number.isSafeInteger(saved.savedAt) && now - saved.savedAt >= 0 && now - saved.savedAt <= ONBOARDING_TTL_MS;
  } catch { return false; }
}

export function clearOnboardingMetaContinuation({ storage = globalThis.sessionStorage } = {}) {
  try { storage?.removeItem?.(ONBOARDING_KEY); } catch { /* storage unavailable */ }
}

// Quick start on the onboarding welcome screen follows saved backend state.
export function onboardingMetaSetupStage(snapshot) {
  if (snapshot?.active && ['ready', 'stale'].includes(snapshot.active.readiness?.state)) return 'ready';
  if (snapshot?.pending || snapshot?.active) return 'incomplete';
  return 'none';
}

// OGC-533 (owner decision, option 3): authorization the owner must renew
// blocks Ready and campaign progress; a temporary or unconfirmed
// authorization only asks for a re-check and does not block.
const META_AUTHORIZATION_RENEWAL_STATES = new Set(['reauth_required', 'disconnected']);
const META_AUTHORIZATION_RECHECK_STATES = new Set(['temporarily_unavailable', 'unknown', 'unavailable']);

export function metaAuthorizationRequiresRenewal(authorizationState) {
  return META_AUTHORIZATION_RENEWAL_STATES.has(authorizationState);
}

// A verified setup may continue to Targeting unless renewal is required.
export function companyMetaPanelAllowsContinue(panelStatus) {
  return ['ready', 'recheck', 'authorization_checking'].includes(panelStatus?.state);
}

// The campaign may not leave the Meta step while its Meta destination's
// authorization must be renewed (same condition as the "Renewal needed" tag).
// Without a destination there is nothing to renew yet, so nothing blocks.
export function campaignMetaStepBlockedByRenewal({ binding, authorizationState } = {}) {
  return Boolean(binding) && metaAuthorizationRequiresRenewal(authorizationState);
}

// A draft past the Meta step returns to it while renewal blocks the step.
// Returns the step index to show, or null to stay.
export function campaignMetaRenewalReturnStep({ blocked, draft, stepIndex, metaIndex } = {}) {
  return blocked === true && draft === true && stepIndex > metaIndex ? metaIndex : null;
}

// Moving forward past the Meta step is stopped while renewal blocks it;
// going back, and moving between later steps, never is.
export function campaignMetaStepNavigationBlocked({ blocked, currentIndex, targetIndex, metaIndex } = {}) {
  return blocked === true && currentIndex <= metaIndex && targetIndex > metaIndex;
}

// The company panels' single readiness tag (OGC-527). Backend readiness wins;
// connection problems and unfinished candidates never read as Ready.
export function companyMetaPanelStatus({ binding, readiness = binding?.readiness, authorizationState = 'valid', pending = null } = {}) {
  if (authorizationState === 'missing' && !binding) return { state: 'disconnected', label: 'Not connected', tone: 'pending' };
  // Renewal is the root problem whatever the last check said.
  if (binding && metaAuthorizationRequiresRenewal(authorizationState)) return { state: 'renewal_required', label: 'Renewal needed', tone: 'pending' };
  const summary = setupSummaryStatus(binding, readiness);
  if (summary.state === 'ready') {
    if (META_AUTHORIZATION_RECHECK_STATES.has(authorizationState)) return { state: 'recheck', label: 'Re-check recommended', tone: 'pending' };
    if (authorizationState === 'checking') return { state: 'authorization_checking', label: 'Checking…', tone: 'pending' };
    return { state: 'ready', label: 'Ready', tone: 'ready' };
  }
  if (summary.state === 'checking') return { state: 'checking', label: 'Checking…', tone: 'pending' };
  if (summary.state === 'stale') return { state: 'stale', label: 'Re-check recommended', tone: 'pending' };
  if (!binding && pending) {
    return pending.readiness?.state === 'needs_attention' && (pending.operationId || pending.confirmedAt)
      ? { state: 'needs_attention', label: 'Needs attention', tone: 'pending' }
      : { state: 'in_progress', label: 'In progress', tone: 'pending' };
  }
  if (!binding) return { state: 'incomplete', label: 'Setup needed', tone: 'pending' };
  return { state: 'needs_attention', label: 'Needs attention', tone: 'pending' };
}

export function existingCampaignCount(response) {
  const all = response?.statusCounts?.all;
  if (Number.isSafeInteger(all) && all >= 0) return all;
  return Array.isArray(response?.campaigns) && response.campaigns.length === 0 ? 0 : null;
}

export function safeMetaTermsUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'www.facebook.com' && url.pathname === '/ads/leadgen/tos/' && !url.username && !url.password
      ? 'https://www.facebook.com/ads/leadgen/tos/' : '';
  } catch { return ''; }
}
